import React, { createRef, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { selectFolders } from '../../redux/reducers/folderReducer';
import { CardSize } from '../../Types/enums';
import { Folder } from '../../Types/folder';
import Card from '../Card/Card';
import FolderIcon from './FolderIcon';
import FolderOptionsMenu from './FolderOptionsMenu/FolderOptionsMenu';

type FolderCardProps = {
  folder: Folder;
  searchQuery: string;
  IsSearchFocused: boolean;
  onFolderCardClick: (folderId?: string, shouldFetchFolders?: boolean) => void;
  size?: CardSize;
};

const FolderCard = ({
  onFolderCardClick,
  folder,
  searchQuery,
  IsSearchFocused,
  size = CardSize.MEDIUM,
}: FolderCardProps) => {
  const { currentFolder } = useSelector(selectFolders);
  const currentFolderCardRef = createRef<HTMLDivElement>();

  const isElementInView = (element: HTMLElement): boolean => {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };

  useEffect(() => {
    const getScrollParent = (element: HTMLElement): HTMLElement | null => {
      if (!element) {
        return document.documentElement;
      }

      const isElementScrollable = element.scrollHeight > element.clientHeight;
      if (isElementScrollable) {
        return element;
      }

      return element.parentElement ? getScrollParent(element.parentElement) : null;
    };

    const element = currentFolderCardRef.current;
    if (element && !isElementInView(element) && !IsSearchFocused) {
      // We use a setTimeout here to push this DOM update to the next event loop,
      // ensuring it doesn't get overriden
      setTimeout(() => {
        if (!isElementInView(element)) {
          const elementRect = element.getBoundingClientRect();
          const scrollParent = getScrollParent(element);

          if (scrollParent) {
            const targetScrollTop =
              elementRect.top +
              scrollParent.scrollTop -
              scrollParent.clientHeight / 2 +
              elementRect.height / 2;

            scrollParent.scrollTo({
              top: targetScrollTop,
            });
          }
        }
      }, 100);
    }
  }, [currentFolder, currentFolderCardRef, IsSearchFocused]);

  const isCurrentlySelected = currentFolder?.id === folder.id;

  return (
    <Card
      type="folder"
      size={size}
      title={folder.name}
      selected={isCurrentlySelected}
      pinned={folder.pinned}
      matchingMessage={folder.description}
      searchQuery={searchQuery}
      CardContextMenu={<FolderOptionsMenu folder={folder} horizontalOptions />}
      onCardClick={() => onFolderCardClick(folder.id, false)}
      icon={<FolderIcon currentFolder={folder} />}
      ref={isCurrentlySelected ? currentFolderCardRef : undefined}
    />
  );
};

export default FolderCard;
